import { Building2, Hospital, LayoutDashboard, Package, ShieldCheck, Users, type LucideIcon } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { AdminDashboardSectionLabel } from "../components/admin-widgets/AdminDashboardSections";
import { InvoiceDistributionChart } from "../components/admin-widgets/InvoiceDistributionChart";
import { MonthlyVolumeChart } from "../components/admin-widgets/MonthlyVolumeChart";
import { SubmittedInvoicesTable } from "../components/admin-widgets/SubmittedInvoicesTable";
import { useAdminDashboard } from "../hooks/useAdminDashboard";
import { useDashboardSummary } from "../hooks/useDashboardSummary";
import { DashboardFiltersBar } from "../components/dashboard/DashboardFiltersBar";
import { DashboardStatusCards } from "../components/dashboard/DashboardStatusCards";
import { KpiCard, KpiGrid, type KpiTone } from "../components/KpiCard";

type Tile = { label: string; value: number; icon: LucideIcon; href: string; tone: KpiTone };

function Tiles({ tiles, columns }: { tiles: Tile[]; columns: string }) {
  return (
    <KpiGrid className={`grid-cols-1 sm:grid-cols-2 ${columns}`}>
      {tiles.map((tile) => (
        <KpiCard key={tile.label} tone={tile.tone} icon={tile.icon} label={tile.label} value={tile.value} href={tile.href} />
      ))}
    </KpiGrid>
  );
}

/**
 * Tableau de bord Admin : filtres (assureur, prestataire, entreprise, package) appliqués aux indicateurs clés
 * (5 cartes de factures, 4 cartes de demandes de validation) ; vue d'ensemble de la plateforme, graphiques
 * et dernières factures.
 */
export function AdminDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.adminDashboard;
  const { data, loading, error } = useAdminDashboard();
  const { filters, setFilters, summary: counters, error: summaryError } = useDashboardSummary("admin");
  const overview = data.overview;
  const summary = labels.summary
    .replace("{invoices}", String(data.summary.totalInvoices))
    .replace("{admissions}", String(data.summary.admissions));

  const overviewTiles: Tile[] = [
    { label: "Assureurs", value: overview.insurers, icon: ShieldCheck, href: "/dashboard/insurers", tone: "sky" },
    { label: "Prestataires", value: overview.providers, icon: Hospital, href: "/dashboard/providers", tone: "pink" },
    { label: "Assurés actifs", value: overview.activeInsured, icon: Users, href: "/dashboard/companies", tone: "mint" },
    { label: "Packages actifs", value: overview.activePackages, icon: Package, href: "/dashboard/claims/packages", tone: "purple" },
    { label: "Entreprises", value: overview.companies, icon: Building2, href: "/dashboard/companies", tone: "amber" },
  ];

  return (
    <div className="space-y-5">
      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={summary}
        icon={LayoutDashboard}
        badge={data.header.roleBadge || labels.badge}
        date={data.header.displayDate}
      />

      <DashboardFiltersBar filters={filters} options={counters?.options ?? null} onChange={setFilters} />
      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}
      {summaryError ? <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{summaryError}</p> : null}
      {loading ? <p className="text-sm text-muted">Chargement…</p> : null}

      <div>
        <AdminDashboardSectionLabel>{labels.keyIndicators}</AdminDashboardSectionLabel>
        <DashboardStatusCards summary={counters} />
      </div>

      <div>
        <AdminDashboardSectionLabel>Vue d'ensemble de la plateforme</AdminDashboardSectionLabel>
        <Tiles tiles={overviewTiles} columns="lg:grid-cols-5" />
      </div>

      <div>
        <AdminDashboardSectionLabel>{labels.sections.charts}</AdminDashboardSectionLabel>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <InvoiceDistributionChart data={data.invoiceDistribution} />
          <MonthlyVolumeChart />
        </div>
      </div>

      <div>
        <AdminDashboardSectionLabel>{labels.sections.invoices}</AdminDashboardSectionLabel>
        <SubmittedInvoicesTable rows={data.submittedInvoices} />
      </div>
    </div>
  );
}
